import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { settings } from "../../helpers/settings";
import authHeader from "../../helpers/functions/auth-header";
import { toast } from "../../helpers/swal";

const EmbroideryWorkspacePage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creatingLabel, setCreatingLabel] = useState({});

  const logout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/");
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(
        `${settings.apiURL}/get_embroidery_orders_combined.php`,
        {
          headers: authHeader(),
        }
      );

      if (resp.data?.success && Array.isArray(resp.data.orders)) {
        setOrders(resp.data.orders);
      } else {
        setOrders([]);
        toast("Embroidery orders not found", "info");
      }
    } catch (err) {
      console.error("Failed to fetch embroidery orders", err);
      toast("Failed to fetch embroidery orders", "error");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const filteredItems = (order) =>
    order.items?.filter(
      (item) =>
        item.name && !["discount", "shipping"].includes(item.name.toLowerCase())
    ) || [];

  const handleCreateLabel = async (order) => {
    if (!order.orderId || !order.orderNumber) {
      toast("Invalid order data", "error");
      return;
    }

    // Label zaten oluşturulmuşsa uyar
    if (order.label_created) {
      toast("Label already created for this order", "info");
      return;
    }

    // Design upload edilmemişse uyar
    if (!order.design_uploaded) {
      toast("Please upload design first", "warning");
      return;
    }

    setCreatingLabel((prev) => ({ ...prev, [order.orderId]: true }));

    try {
      let resp;

      if (order.source === "maya") {
        // Maya order - doğrudan Maya API'sine
        resp = await axios.post(
          `${settings.apiURL}/create_shipstation_label_maya.php`,
          {
            orderId: order.orderId,
            orderNumber: order.orderNumber,
          },
          { headers: authHeader() }
        );
      } else if (order.source === "customzon") {
        // Customzon order - Maya proxy üzerinden Customzon API'sine
        resp = await axios.post(
          `${settings.apiURL}/create_shipstation_label_customzon_proxy.php`,
          {
            orderId: order.orderId,
            orderNumber: order.orderNumber,
          },
          { headers: authHeader() }
        );
      }

      if (resp.data.success) {
        // Test mode kontrolü
        const isTestMode = resp.data.test_mode === true;
        const successMessage = isTestMode
          ? "✅ TEST LABEL created successfully! (No charges applied)"
          : "✅ Label created successfully!";

        toast(successMessage, "success");

        // PDF'i yeni sekmede aç
        if (resp.data.labelDownloadUrl) {
          window.open(resp.data.labelDownloadUrl, "_blank");
        }

        // Order listesini güncelle (local state)
        setOrders((prevOrders) =>
          prevOrders.map((o) =>
            o.orderId === order.orderId
              ? {
                  ...o,
                  label_created: true,
                  label_file_path: resp.data.label_file_path,
                  tracking_number: resp.data.tracking_number,
                  labelDownloadUrl: resp.data.labelDownloadUrl,
                }
              : o
          )
        );
      } else {
        toast(resp.data.message || "Label creation failed", "error");
      }
    } catch (err) {
      console.error("Label creation error:", err);
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Label creation failed. Please try again.";
      toast(errorMessage, "error");
    } finally {
      setCreatingLabel((prev) => ({ ...prev, [order.orderId]: false }));
    }
  };

  const handleDownloadLabel = (order) => {
    if (!order.labelDownloadUrl) {
      toast("Label URL not found", "error");
      return;
    }
    window.open(order.labelDownloadUrl, "_blank");
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f7f7f7",
        padding: "16px",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: "16px" }}>
        <button
          onClick={logout}
          style={{
            background: "#ff7043",
            color: "#fff",
            border: "none",
            padding: "10px 18px",
            borderRadius: "6px",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          Logout
        </button>
      </div>

      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          background: "#fff",
          borderRadius: "10px",
          padding: "16px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "12px",
          }}
        >
          <h3 style={{ margin: 0 }}>Embroidery Orders (Maya + Customzon)</h3>
          <button
            onClick={fetchOrders}
            className="btn btn-sm btn-primary"
            style={{ minWidth: "100px" }}
            disabled={loading}
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        {loading ? (
          <p>Loading...</p>
        ) : orders.length === 0 ? (
          <p>No embroidery orders found.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="table table-sm table-striped table-bordered">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Source</th>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Items</th>
                  <th>Total Qty</th>
                  <th>Production</th>
                  <th>Label Status</th>
                  <th>Internal Notes</th>
                  <th>Label</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order, idx) => {
                  const items = filteredItems(order);
                  const totalQty = items.reduce(
                    (sum, item) => sum + (item.quantity || 0),
                    0
                  );

                  return (
                    <tr key={order.orderId || idx}>
                      <td>{idx + 1}</td>
                      <td>
                        <span
                          style={{
                            backgroundColor:
                              order.source === "maya" ? "#007bff" : "#28a745",
                            color: "white",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: "bold",
                          }}
                        >
                          {order.source === "maya" ? "Maya" : "Customzon"}
                        </span>
                      </td>
                      <td>
                        <button
                          style={{
                            background: "none",
                            border: "none",
                            color: "#007bff",
                            textDecoration: "underline",
                            cursor: "pointer",
                            padding: 0,
                          }}
                          onClick={() =>
                            navigate(`/embroidery-order/${order.orderId}`, {
                              state: { orders },
                            })
                          }
                        >
                          {order.orderNumber}
                        </button>
                      </td>
                      <td>{order.shipTo?.name || "-"}</td>
                      <td>{items.length}</td>
                      <td>{totalQty}</td>
                      <td
                        style={{
                          backgroundColor:
                            order.production_status === "in_progress"
                              ? "#ffc107"
                              : order.production_status === "completed"
                              ? "#28a745"
                              : "#6c757d",
                          color: "white",
                          textAlign: "center",
                          fontWeight: "bold",
                          fontSize: "12px",
                        }}
                      >
                        {order.production_status === "in_progress"
                          ? "IN PROGRESS"
                          : order.production_status === "completed"
                          ? "COMPLETED"
                          : "PENDING"}
                      </td>
                      <td
                        style={{
                          backgroundColor: order.label_created
                            ? "#198754"
                            : order.design_uploaded
                            ? "#ffc107"
                            : "#6c757d",
                          color: "white",
                          textAlign: "center",
                          fontWeight: "bold",
                          fontSize: "12px",
                        }}
                      >
                        {order.label_created
                          ? "Label Created"
                          : order.design_uploaded
                          ? "Design Uploaded"
                          : "Pending"}
                      </td>
                      <td
                        style={{
                          maxWidth: "200px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={order.internalNotes || "-"}
                      >
                        {order.internalNotes || "-"}
                      </td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            gap: "4px",
                            flexDirection: "column",
                          }}
                        >
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() =>
                              navigate(`/embroidery-order/${order.orderId}`, {
                                state: { orders },
                              })
                            }
                          >
                            View Details
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmbroideryWorkspacePage;
